import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import { formatValor } from "../../../../../modulos/monitoreo/sesion/formatIncidencia";
import { LoteSesionDetalleResponse } from "../../../../../modulos/monitoreo/sesion/response/SesionDetalleResponse";
import {
  ESTADO_LABEL,
  MonitoreoEstado,
} from "../../../../../modulos/monitoreo/sesion/semaforo";
import {
  ESTADO_COLOR,
  PALETA_CATEGORICA,
} from "../dashboard/monitoreoAnalisisPaleta";

/** Geometría del gráfico de barras, en unidades del `viewBox`. */
const BARRAS = {
  ancho: 720,
  alto: 260,
  margenIzq: 44,
  margenDer: 12,
  margenSup: 12,
  margenInf: 52,
};

/**
 * Barras agrupadas: incidencia por lote, una barra por plaga dentro de cada
 * lote.
 *
 * Se genera como SVG estático y no con una librería de gráficos porque el
 * reporte se abre como documento autocontenido en su propia pestaña, sin
 * acceso al bundle de la app. Como efecto secundario imprime nítido a
 * cualquier resolución y no depende de que un `<canvas>` haya terminado de
 * dibujarse cuando el usuario manda a imprimir.
 *
 * @param lotes  Lotes de la sesión.
 * @param plagas Plagas a graficar (ya acotadas por el llamador).
 */
export function buildSvgBarras(
  lotes: LoteSesionDetalleResponse[],
  plagas: MonitoreoPlagaEnfermedadItem[],
): string {
  if (lotes.length === 0 || plagas.length === 0) {
    return `<p class="rp-vacio">Sin incidencias para graficar.</p>`;
  }

  const { ancho, alto, margenIzq, margenDer, margenSup, margenInf } = BARRAS;
  const anchoUtil = ancho - margenIzq - margenDer;
  const altoUtil = alto - margenSup - margenInf;

  const maximo = Math.max(
    ...lotes.flatMap((lote) => plagas.map((plaga) => lote.inc[plaga.id] ?? 0)),
    1,
  );
  const escalaY = (valor: number) => altoUtil - (valor / maximo) * altoUtil;

  const anchoGrupo = anchoUtil / lotes.length;
  // 12% de aire a cada lado del grupo, para que dos lotes contiguos no se
  // toquen (la guía pide separación entre marcas adyacentes).
  const anchoBarras = anchoGrupo * 0.76;
  const anchoBarra = anchoBarras / plagas.length;

  const grillaY = [0, 0.25, 0.5, 0.75, 1].map((fraccion) => {
    const y = margenSup + altoUtil * fraccion;
    const valor = formatValor(maximo * (1 - fraccion));
    return `<line x1="${margenIzq}" y1="${y}" x2="${ancho - margenDer}" y2="${y}" stroke="#e4e3dc" stroke-width="1" />
      <text x="${margenIzq - 6}" y="${y + 3}" text-anchor="end" class="rp-svg-tick">${valor}</text>`;
  });

  const grupos = lotes.map((lote, indiceLote) => {
    const xGrupo =
      margenIzq + anchoGrupo * indiceLote + (anchoGrupo - anchoBarras) / 2;

    const barras = plagas.map((plaga, indicePlaga) => {
      const valor = lote.inc[plaga.id] ?? 0;
      const x = xGrupo + anchoBarra * indicePlaga;
      const y = margenSup + escalaY(valor);
      const altura = Math.max(margenSup + altoUtil - y, 0);
      const color = PALETA_CATEGORICA[indicePlaga % PALETA_CATEGORICA.length];
      // 1px de separación entre barras vecinas y esquinas superiores suaves.
      return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${Math.max(anchoBarra - 1, 1).toFixed(1)}" height="${altura.toFixed(1)}" rx="2" fill="${color}"><title>${lote.nombre} · ${plaga.nombreComun}: ${formatValor(valor)}</title></rect>`;
    });

    const etiqueta = `<text x="${(xGrupo + anchoBarras / 2).toFixed(1)}" y="${alto - margenInf + 16}" text-anchor="middle" class="rp-svg-label">${lote.nombre}</text>`;
    return barras.join("") + etiqueta;
  });

  const leyenda = plagas
    .map((plaga, indice) => {
      const color = PALETA_CATEGORICA[indice % PALETA_CATEGORICA.length];
      return `<span class="rp-legend-item"><span class="rp-legend-swatch" style="background:${color}"></span>${plaga.nombreComun}</span>`;
    })
    .join("");

  return `<svg viewBox="0 0 ${ancho} ${alto}" class="rp-svg" role="img"
      aria-label="Incidencia por lote y plaga, conteo promedio por árbol">
      ${grillaY.join("")}
      ${grupos.join("")}
    </svg>
    <div class="rp-legend">${leyenda}</div>`;
}

/**
 * Dona de distribución de alertas: cuántas combinaciones lote × plaga cayeron
 * en cada estado del semáforo.
 *
 * Se dibuja con `stroke-dasharray` sobre un círculo de circunferencia 100, así
 * cada segmento se expresa directamente como porcentaje y no hace falta
 * calcular arcos.
 */
export function buildSvgDona(
  conteos: Record<MonitoreoEstado, number>,
): string {
  const total = conteos.alert + conteos.warn + conteos.ok;
  if (total === 0) {
    return `<p class="rp-vacio">Sin incidencias registradas.</p>`;
  }

  // Radio elegido para que la circunferencia sea 100 (2·π·r = 100).
  const radio = 15.9155;
  const orden: MonitoreoEstado[] = ["alert", "warn", "ok"];

  let offset = 0;
  const segmentos = orden.map((estado) => {
    const porcentaje = (conteos[estado] / total) * 100;
    const segmento = `<circle cx="21" cy="21" r="${radio}" fill="none"
        stroke="${ESTADO_COLOR[estado]}" stroke-width="6"
        stroke-dasharray="${porcentaje.toFixed(2)} ${(100 - porcentaje).toFixed(2)}"
        stroke-dashoffset="${(-offset).toFixed(2)}"
        transform="rotate(-90 21 21)"><title>${ESTADO_LABEL[estado]}: ${conteos[estado]}</title></circle>`;
    offset += porcentaje;
    return segmento;
  });

  // La leyenda lleva el conteo en texto: el estado nunca se comunica solo por
  // color.
  const leyenda = orden
    .map(
      (estado) =>
        `<span class="rp-legend-item"><span class="rp-legend-swatch" style="background:${ESTADO_COLOR[estado]}"></span>${ESTADO_LABEL[estado]} · <strong>${conteos[estado]}</strong></span>`,
    )
    .join("");

  return `<svg viewBox="0 0 42 42" class="rp-svg rp-svg--dona" role="img"
      aria-label="Distribución de combinaciones lote por plaga según estado">
      <circle cx="21" cy="21" r="${radio}" fill="none" stroke="#e4e3dc" stroke-width="6" />
      ${segmentos.join("")}
      <text x="21" y="21.5" text-anchor="middle" class="rp-svg-dona-total">${total}</text>
      <text x="21" y="24.8" text-anchor="middle" class="rp-svg-dona-sub">casos</text>
    </svg>
    <div class="rp-legend rp-legend--vertical">${leyenda}</div>`;
}
